import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WeekSpine } from '../components/WeekSpine';
import { Counts, FloorMap } from '../components/FloorMap';
import { RangeCalendar } from '../components/RangeCalendar';
import { ResourceList } from '../components/ResourceList';
import { BookingPanel } from '../components/BookingPanel';
import { useIsMobile } from '../useIsMobile';
import {
  useAreas,
  useAvailability,
  useCancelBooking,
  useCreateBooking,
  useCreateRangeBooking,
  useMyBookings,
  usePreferredAreas,
  useRangeAvailability,
  useSetPreferredArea,
  useSites,
} from '../queries';
import { today, weekStart, addDays, describe } from '../dates';
import type { AvailableResource, RangeResource, Slot } from '../types';

type Kind = 'office' | 'parking';
type Mode = 'day' | 'range';

const laterOf = (a: string, b: string | null | undefined) => (b && b > a ? b : a);
const earlierOf = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a) return b ?? null;
  if (!b) return a;
  return a < b ? a : b;
};

/**
 * A desk seen across a range, dressed as a single-day one.
 *
 * FloorMap and ResourceList speak AvailableResource, and both work
 * well. Teaching them a second shape would mean touching the component
 * every desk booking in the building depends on, to serve the rarer
 * case. The mapping is honest enough: free every day reads as free,
 * free on some days reads as half free, free on none reads as taken.
 */
function asAvailable(resource: RangeResource, days: number, slot: Slot): AvailableResource {
  const state = resource.freeDays === 0
    ? (resource.restricted ? 'reserved' : 'taken')
    : resource.freeDays === days
      ? 'free'
      : 'am_free';

  return {
    id: resource.id,
    label: resource.label,
    zone: resource.zone,
    tags: resource.tags,
    map: resource.map,
    state,
    freeSlots: resource.bookable ? [slot] : [],
    heldBy: null,
    mine: false,
    bookable: resource.bookable,
  };
}

export function BookPage() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? today();
  const kind = (params.get('kind') as Kind | null) ?? 'office';

  const isMobile = useIsMobile();

  const [mode, setMode] = useState<Mode>('day');
  const [rangeFrom, setRangeFrom] = useState<string | null>(null);
  const [rangeTo, setRangeTo] = useState<string | null>(null);
  const [rangeResult, setRangeResult] = useState<
    { booked: number; skipped: { date: string; reason: string }[] } | null
  >(null);

  const [siteId, setSiteId] = useState<string | undefined>();
  const [areaId, setAreaId] = useState<string | undefined>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot>('full');
  const [view, setView] = useState<'map' | 'list'>('map');
  const [onlyFree, setOnlyFree] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A phone shows the date and what you hold, then the plan on its own
  // screen. Stacking both means the map starts halfway down with no
  // room left to actually see it.
  const [step, setStep] = useState<'when' | 'where'>('when');

  const sites = useSites();

  // Most people have exactly one office and never see a choice. Admins
  // see every site, and anyone with a grant sees theirs plus the office
  // they were given. /sites sorts the person's own office first, so the
  // default is right without asking.
  useEffect(() => {
    const list = sites.data;
    if (!list || list.length === 0) return;
    if (list.some((candidate) => candidate.id === siteId)) return;
    setSiteId(list[0]?.id);
    setAreaId(undefined);
    setSelectedId(null);
  }, [sites.data, siteId]);

  const site = sites.data?.find((candidate) => candidate.id === siteId)
    ?? sites.data?.[0];
  const areas = useAreas(site?.id);

  const preferred = usePreferredAreas();
  const setPreferred = useSetPreferredArea();

  const areasOfKind = useMemo(
    () => (areas.data ?? []).filter((area) => area.kind === kind),
    [areas.data, kind],
  );

  useEffect(() => {
    if (areasOfKind.length === 0) return;
    if (areasOfKind.some((area) => area.id === areaId)) return;

    // A preference is only applied when it is one of the areas this
    // person can actually see. One pointing at another site is ignored
    // without needing to be checked, which is what keeps this correct
    // once there is more than one site.
    const usual = preferred.data?.[kind];
    const fallback = areasOfKind[0]?.id;
    setAreaId(
      usual && areasOfKind.some((area) => area.id === usual) ? usual : fallback,
    );
    setSelectedId(null);
  }, [areasOfKind, areaId, preferred.data, kind]);

  // What the calendar may offer. The booking window and, at a visiting
  // office, the grant dates. Both are enforced in the database anyway;
  // stopping here means somebody is not invited to pick a day that will
  // then be refused.
  const minDate = laterOf(
    site?.today ?? today(),
    site && !site.is_home ? site.granted_from : null,
  );
  const maxDate = earlierOf(
    site?.bookable_until,
    site && !site.is_home ? site.granted_to : null,
  );

  const area = areasOfKind.find((candidate) => candidate.id === areaId);
  const availability = useAvailability(areaId, date);
  const rangeAvailability = useRangeAvailability(
    mode === 'range' ? areaId : undefined,
    rangeFrom ?? '',
    rangeTo ?? '',
    slot,
  );

  const monday = weekStart(date);
  const myBookings = useMyBookings(monday, addDays(monday, 4));

  const create = useCreateBooking();
  const createRange = useCreateRangeBooking();
  const cancel = useCancelBooking();

  const rangeDays = rangeAvailability.data?.days ?? 0;
  const rangeResources = rangeAvailability.data?.resources ?? [];

  const resources: AvailableResource[] = mode === 'range'
    ? rangeResources.map((resource) => asAvailable(resource, rangeDays, slot))
    : (availability.data?.resources ?? []);

  const selected = resources.find((resource) => resource.id === selectedId) ?? null;
  const heldToday = (myBookings.data ?? []).filter((booking) => booking.date === date);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };

  const startRange = () => {
    setMode('range');
    setSelectedId(null);
    setRangeResult(null);
    setError(null);
    if (!rangeFrom || !rangeTo || rangeFrom < minDate) {
      const start = laterOf(minDate, date >= minDate ? date : null);
      setRangeFrom(start);
      setRangeTo(start);
    }
  };

  const startDay = () => {
    setMode('day');
    setSelectedId(null);
    setRangeResult(null);
    setError(null);
  };

  const choose = (resource: AvailableResource) => {
    setError(null);
    setRangeResult(null);
    setSelectedId(resource.id);
    if (mode === 'day') setSlot(resource.freeSlots[0] ?? 'full');
  };

  const confirm = async () => {
    if (!selected) return;
    setError(null);
    try {
      await create.mutateAsync({ resourceId: selected.id, date, slot });
      setSelectedId(null);
      // Back to the overview, which now shows the new booking.
      if (isMobile) setStep('when');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not book that.');
    }
  };

  const confirmRange = async () => {
    if (!selected || !rangeFrom || !rangeTo) return;
    setError(null);
    setRangeResult(null);
    try {
      const result = await createRange.mutateAsync({
        resourceId: selected.id,
        from: rangeFrom,
        to: rangeTo,
        slot,
      });
      setRangeResult({ booked: result.created.length, skipped: result.skipped });
      setSelectedId(null);
      if (isMobile) setStep('when');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not book those days.');
    }
  };

  const cancelBooking = async (bookingId: string) => {
    setError(null);
    try {
      await cancel.mutateAsync(bookingId);
      setSelectedId(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not cancel that.');
    }
  };

  const kindLabel = kind === 'parking' ? 'Parking' : 'Desks';
  const thing = kind === 'parking' ? 'bay' : 'desk';

  const kindTabs = (
    <div className="segmented">
      <button
        aria-pressed={kind === 'office'}
        onClick={() => {
          setParam('kind', 'office');
          setSelectedId(null);
        }}
      >
        Desks
      </button>
      <button
        aria-pressed={kind === 'parking'}
        onClick={() => {
          setParam('kind', 'parking');
          setSelectedId(null);
        }}
      >
        Parking
      </button>
    </div>
  );

  const modeTabs = (
    <div className="segmented">
      <button aria-pressed={mode === 'day'} onClick={startDay}>
        One day
      </button>
      <button aria-pressed={mode === 'range'} onClick={startRange}>
        Several days
      </button>
    </div>
  );

  const isUsual = Boolean(areaId) && preferred.data?.[kind] === areaId;

  // Only rendered when there is a genuine choice, which for most people
  // is never. Changing site clears the floor and the selection: an area
  // id from Oslo means nothing in Amsterdam.
  const siteSelect = (sites.data?.length ?? 0) > 1 ? (
    <select
      value={site?.id ?? ''}
      onChange={(event) => {
        setSiteId(event.target.value);
        setAreaId(undefined);
        setSelectedId(null);
      }}
      aria-label="Office"
    >
      {(sites.data ?? []).map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </select>
  ) : null;

  const floorSelect = areasOfKind.length > 1 ? (
    <div className="row" style={{ gap: 6 }}>
      <select
        value={areaId ?? ''}
        onChange={(event) => {
          setAreaId(event.target.value);
          setSelectedId(null);
        }}
        aria-label={kind === 'parking' ? 'Parking area' : 'Floor'}
      >
        {areasOfKind.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
      <button
        className="usual"
        aria-pressed={isUsual}
        title={
          isUsual
            ? 'This is where the app opens. Tap to stop.'
            : 'Open here every time'
        }
        disabled={!areaId || setPreferred.isPending}
        onClick={() =>
          setPreferred.mutate({ kind, areaId: isUsual ? null : (areaId ?? null) })
        }
      >
        {isUsual ? '★ Usual' : '☆ Set as usual'}
      </button>
    </div>
  ) : null;

  // Two different sentences under the day strip, and at most one shows.
  //
  // A visiting office is visible from the moment the grant is created,
  // which is deliberate -- people plan trips in advance -- but booking
  // outside the granted dates is refused by the database. Saying the
  // dates here is the difference between an obvious rule and a puzzling
  // error at the moment of confirming.
  //
  // The window sentence is guarded on the date, not on the site: a site
  // with no window at all has a null bookable_until, describe() throws
  // on it, and a render throw blanks the whole page.
  const note = site && !site.is_home && site.granted_from && site.granted_to ? (
    <p className="muted">
      You can book at {site.name} from {describe(site.granted_from).toLowerCase()}{' '}
      to {describe(site.granted_to).toLowerCase()}.
    </p>
  ) : site?.bookable_until ? (
    <p className="muted">
      Bookings open {site.max_days_ahead} working days ahead, currently to{' '}
      {describe(site.bookable_until).toLowerCase()}.
    </p>
  ) : null;

  // What happened after a range booking. Days somebody already held are
  // the common reason for a skip, and saying nothing about them would
  // look like the app quietly ignored part of the request.
  const rangeOutcome = rangeResult ? (
    <div className="card" style={{ padding: 12 }}>
      <p>
        {rangeResult.booked > 0
          ? `Booked ${rangeResult.booked} day${rangeResult.booked === 1 ? '' : 's'}.`
          : 'Nothing was booked.'}
      </p>
      {rangeResult.skipped.length > 0 && (
        <ul className="muted" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {rangeResult.skipped.map((skip) => (
            <li key={skip.date}>
              {describe(skip.date)} — {skip.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  const whenPicker = mode === 'range' ? (
    <>
      <RangeCalendar
        from={rangeFrom}
        to={rangeTo}
        minDate={minDate}
        maxDate={maxDate}
        onChange={({ from, to }) => {
          setRangeFrom(from);
          setRangeTo(to);
          setSelectedId(null);
          setRangeResult(null);
        }}
      />
      {note}
    </>
  ) : (
    <>
      <WeekSpine
        date={date}
        onChange={(next) => {
          setParam('date', next);
          setSelectedId(null);
        }}
        bookings={myBookings.data ?? []}
        bookableUntil={site?.bookable_until}
      />
      {note}
    </>
  );

  const loadFailed = sites.isError || areas.isError
    || (mode === 'range' ? rangeAvailability.isError : availability.isError);
  const dataReady = mode === 'range'
    ? Boolean(rangeAvailability.data)
    : Boolean(availability.data);
  const dataPending = mode === 'range'
    ? rangeAvailability.isPending
    : availability.isPending;

  const picker = (
    <>
      {loadFailed && (
        <p className="notice">
          Could not load bookings.{' '}
          <button
            className="quiet"
            style={{ textDecoration: 'underline', padding: 0 }}
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
          , or sign out and back in if it keeps happening.
        </p>
      )}

      {!loadFailed && (
        <>
          {(sites.isPending ||
            areas.isPending ||
            (Boolean(areaId) && dataPending)) && (
            <p className="muted">Loading…</p>
          )}
          {!areas.isPending && areasOfKind.length === 0 && (
            <p className="muted">
              No {kind === 'parking' ? 'parking areas' : 'floors'} are set up yet.
            </p>
          )}
        </>
      )}

      {mode === 'range' && rangeAvailability.data && (
        <p className="muted" style={{ marginBottom: 6 }}>
          {rangeDays} working day{rangeDays === 1 ? '' : 's'} selected. Green is free
          every day, amber only some.
        </p>
      )}

      {dataReady && view === 'map' && area?.map_key && (
        <FloorMap
          mapKey={area.map_key}
          resources={resources}
          selectedId={selectedId}
          onSelect={choose}
        />
      )}

      {dataReady && (view === 'list' || !area?.map_key) && (
        <ResourceList
          resources={resources}
          selectedId={selectedId}
          onSelect={choose}
          onlyFree={onlyFree}
        />
      )}
    </>
  );

  // The range panel is separate from BookingPanel rather than a mode
  // inside it: BookingPanel is built around one date and the bookings
  // already held that day, neither of which means anything here.
  const rangePanel = (
    <div className="card stack" style={{ padding: 14, gap: 10 }}>
      {rangeFrom && rangeTo && (
        <p className="muted">
          {rangeFrom === rangeTo
            ? describe(rangeFrom)
            : `${describe(rangeFrom)} to ${describe(rangeTo)}`}
        </p>
      )}

      {selected ? (
        <>
          <p>
            <strong>{selected.label}</strong>
            {selected.zone ? <span className="muted"> · {selected.zone}</span> : null}
          </p>
          {(() => {
            const picked = rangeResources.find((r) => r.id === selected.id);
            return picked && picked.freeDays < rangeDays ? (
              <p className="muted">
                Free on {picked.freeDays} of {rangeDays} days. The rest will be
                skipped.
              </p>
            ) : null;
          })()}
          <label className="row" style={{ gap: 6, fontSize: 13 }}>
            Time
            <select
              value={slot}
              onChange={(event) => setSlot(event.target.value as Slot)}
            >
              <option value="full">Full day</option>
              <option value="am">08:00 – 12:00</option>
              <option value="pm">12:00 – 16:00</option>
            </select>
          </label>
          <button
            className="primary"
            disabled={createRange.isPending}
            onClick={confirmRange}
          >
            {createRange.isPending ? 'Booking…' : `Book ${rangeDays} day${rangeDays === 1 ? '' : 's'}`}
          </button>
        </>
      ) : (
        <p className="muted">Pick a {thing} to book for these days.</p>
      )}

      {error && <p className="notice">{error}</p>}
      {rangeOutcome}
    </div>
  );

  const panel = mode === 'range' ? rangePanel : (
    <BookingPanel
      date={date}
      resource={selected}
      slot={slot}
      onSlotChange={setSlot}
      onConfirm={confirm}
      existing={heldToday}
      onCancel={cancelBooking}
      pending={create.isPending || cancel.isPending}
      error={error}
      kindLabel={kindLabel}
    />
  );

  // --- phone: the day first, then the space ---------------------------
  if (isMobile) {
    if (step === 'where') {
      return (
        <div className="page step">
          <div className="step-head">
            <button
              className="quiet"
              onClick={() => {
                setStep('when');
                setSelectedId(null);
              }}
            >
              ‹ Back
            </button>
            <span className="step-title">
              {mode === 'range' && rangeFrom && rangeTo && rangeFrom !== rangeTo
                ? `${describe(rangeFrom)} – ${describe(rangeTo)}`
                : describe(mode === 'range' ? (rangeFrom ?? date) : date)}
            </span>
            {siteSelect}
            {floorSelect}
          </div>

          <div className="card" style={{ padding: 10 }}>
            {picker}
          </div>

          {panel}

          <div className="row" style={{ justifyContent: 'center' }}>
            <button className="quiet" onClick={() => setView(view === 'map' ? 'list' : 'map')}>
              {view === 'map' ? 'Show as a list instead' : 'Show the floor plan'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="page step">
        <div className="card panel">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            {kindTabs}
            {siteSelect}
          </div>
          {modeTabs}
          {whenPicker}
        </div>

        {mode === 'range' ? rangeOutcome : panel}

        <button className="primary big" onClick={() => setStep('where')}>
          Choose a {thing}
        </button>
      </div>
    );
  }

  // --- desktop: everything at once ------------------------------------
  return (
    <div className="page fill">
      <div className="card panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          {kindTabs}
          <div className="row" style={{ gap: 6 }}>
            {modeTabs}
            {siteSelect}
            {floorSelect}
          </div>
        </div>

        {whenPicker}
      </div>

      <div className="book-layout">
        <div className="card stack" style={{ padding: 14 }}>
          {mode === 'day' && availability.data && (
            <Counts summary={availability.data.summary} />
          )}

          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="segmented">
              <button aria-pressed={view === 'map'} onClick={() => setView('map')}>
                Map
              </button>
              <button aria-pressed={view === 'list'} onClick={() => setView('list')}>
                List
              </button>
            </div>
            {view === 'list' && (
              <label className="row" style={{ gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={onlyFree}
                  onChange={(event) => setOnlyFree(event.target.checked)}
                />
                Only show free
              </label>
            )}
          </div>

          {picker}
        </div>

        <div className="panel-slot">{panel}</div>
      </div>
    </div>
  );
}
